import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Check, X, MessageSquare, Phone, Loader2, Clock, DollarSign, ChevronDown, ChevronRight, Trash2, RotateCcw } from 'lucide-react';
import { useBrokerOffers, BrokerOffer } from '@/hooks/useBrokerOffers';
import { useAuth } from '@/contexts/AuthContext';
import { getDismissed, dismissOffer, restoreOffer } from '@/lib/offerDismissals';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { format, formatDistanceToNow } from 'date-fns';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVerificationWarning } from '@/hooks/useVerificationWarning';
import { VerificationWarningModal } from '@/components/verification/VerificationWarningModal';

const STATUS_LABEL: Record<string, { label: string; tone: string }> = {
  pending: { label: 'Pending', tone: 'bg-lr-cyan/10 text-lr-cyan border-lr-cyan/30' },
  accepted_by_carrier: { label: 'Carrier Accepted', tone: 'bg-lr-cyan/10 text-lr-cyan border-lr-cyan/30' },
  countered: { label: 'You Countered', tone: 'bg-amber-100 text-amber-700 border-amber-300' },
  accepted: { label: 'Awarded', tone: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  rejected: { label: 'Declined', tone: 'bg-muted text-muted-foreground border-border' },
};
const isTerminal = (s: string) => s === 'accepted' || s === 'rejected';
const isActionable = (s: string) => s === 'pending' || s === 'accepted_by_carrier';

const BrokerOffersSection: React.FC = () => {
  const { offers, loading, respondToOffer, refetch } = useBrokerOffers();
  const { user } = useAuth();
  // Set of offer ids currently in-flight OR locally locked after a terminal action.
  // Locked rows hide action buttons immediately, even before refetch lands.
  const [lockedOfferIds, setLockedOfferIds] = useState<Set<string>>(new Set());
  const [counterDialogOpen, setCounterDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<BrokerOffer | null>(null);
  const [counterAmount, setCounterAmount] = useState('');
  const [counterNotes, setCounterNotes] = useState('');
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineNotes, setDeclineNotes] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showCleared, setShowCleared] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(() => getDismissed(user?.id || ''));
  const isMobile = useIsMobile();
  const { showWarningOrRun, modalProps } = useVerificationWarning();

  // Re-load dismissals when user changes
  useEffect(() => {
    setDismissed(getDismissed(user?.id || ''));
  }, [user?.id]);

  const handleClear = (offerId: string) => {
    if (!user?.id) return;
    setDismissed(dismissOffer(user.id, offerId));
  };
  const handleRestore = (offerId: string) => {
    if (!user?.id) return;
    setDismissed(restoreOffer(user.id, offerId));
  };

  const lockOffer = (id: string) => setLockedOfferIds((prev) => { const n = new Set(prev); n.add(id); return n; });
  const unlockOffer = (id: string) => setLockedOfferIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
  const isLocked = (id: string) => lockedOfferIds.has(id);

  const handleAccept = async (offer: BrokerOffer) => {
    if (isLocked(offer.id)) return;
    lockOffer(offer.id);
    const ok = await respondToOffer(offer.id, 'accept');
    // On success keep locked — the refetch will move it into history.
    if (!ok) unlockOffer(offer.id);
  };

  const handleDeclineClick = (offer: BrokerOffer) => {
    if (isLocked(offer.id)) return;
    setSelectedOffer(offer);
    setDeclineDialogOpen(true);
  };

  const handleDeclineConfirm = async () => {
    if (!selectedOffer) return;
    const id = selectedOffer.id;
    lockOffer(id);
    const ok = await respondToOffer(id, 'decline', undefined, declineNotes);
    if (!ok) unlockOffer(id);
    setDeclineDialogOpen(false);
    setSelectedOffer(null);
    setDeclineNotes('');
  };

  const handleCounterClick = (offer: BrokerOffer) => {
    if (isLocked(offer.id)) return;
    setSelectedOffer(offer);
    setCounterAmount(offer.rate_amount.toString());
    setCounterNotes('');
    setCounterDialogOpen(true);
  };

  const handleCounterSubmit = async () => {
    if (!selectedOffer || !counterAmount) return;
    const id = selectedOffer.id;
    lockOffer(id);
    const ok = await respondToOffer(id, 'counter', parseFloat(counterAmount), counterNotes);
    if (!ok) unlockOffer(id);
    setCounterDialogOpen(false);
    setSelectedOffer(null);
    setCounterAmount('');
    setCounterNotes('');
  };

  const getCarrierName = (offer: BrokerOffer) => {
    if (offer.carrier_profile) {
      const { first_name, last_name, email } = offer.carrier_profile;
      if (first_name || last_name) {
        return `${first_name || ''} ${last_name || ''}`.trim();
      }
      return email.split('@')[0];
    }
    return 'Unknown Carrier';
  };

  const getTimeRemaining = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const expires = new Date(expiresAt);
    if (expires < new Date()) return 'Expired';
    return formatDistanceToNow(expires, { addSuffix: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">No carrier offers yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            When carriers accept or negotiate your loads, their offers will appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  const activeOffers = offers.filter(o => !isTerminal(o.status));
  const allHistoryOffers = offers.filter(o => isTerminal(o.status));
  const historyOffers = showCleared
    ? allHistoryOffers
    : allHistoryOffers.filter(o => !dismissed.has(o.id));
  const clearedCount = allHistoryOffers.length - historyOffers.length;

  // Auto-open history if there's a recent (<24h) terminal row not yet cleared.
  const DAY_MS = 24 * 60 * 60 * 1000;
  const hasRecentHistory = useMemo(
    () => historyOffers.some(o => Date.now() - new Date(o.created_at).getTime() < DAY_MS),
    [historyOffers]
  );
  useEffect(() => {
    if (hasRecentHistory) setShowHistory(true);
  }, [hasRecentHistory]);

  const renderMobileCard = (offer: BrokerOffer) => {
    const locked = isLocked(offer.id);
    const showActions = isActionable(offer.status) && !locked;
    return (
      <Card key={offer.id} className={cn("bg-card border-border", offer.status === 'accepted' && "border-emerald-300 bg-emerald-50/30")}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium text-foreground">#{offer.load?.load_number}</span>
              <Badge variant="outline" className={cn("ml-2", STATUS_LABEL[offer.status]?.tone || 'bg-muted text-muted-foreground border-border')}>
                {STATUS_LABEL[offer.status]?.label || offer.status}
              </Badge>
            </div>
            <span className="text-lr-yellow font-bold">${offer.rate_amount.toLocaleString()}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            {offer.load?.origin_city}, {offer.load?.origin_state} → {offer.load?.destination_city}, {offer.load?.destination_state}
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Carrier: </span>
            <span className="text-foreground">{getCarrierName(offer)}</span>
          </div>
          {offer.expires_at && !isTerminal(offer.status) && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span>Expires {getTimeRemaining(offer.expires_at)}</span>
            </div>
          )}
          {offer.notes && (
            <div className="text-xs bg-muted/40 rounded p-2 border border-border">
              <span className="text-muted-foreground font-semibold uppercase tracking-wide text-[10px]">Carrier note</span>
              <p className="mt-0.5 text-foreground whitespace-pre-wrap">{offer.notes}</p>
            </div>
          )}
          {showActions ? (
            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <Button size="sm" className="flex-1 bg-primary hover:bg-primary/85 text-primary-foreground" onClick={() => showWarningOrRun(() => handleAccept(offer))} disabled={locked}>
                {locked ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Check className="w-4 h-4 mr-1" />Award</>)}
              </Button>
              <Button size="sm" variant="outline" className="flex-1" onClick={() => showWarningOrRun(() => handleCounterClick(offer))} disabled={locked}>
                <DollarSign className="w-4 h-4 mr-1" />Counter
              </Button>
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => showWarningOrRun(() => handleDeclineClick(offer))} disabled={locked}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : locked && !isTerminal(offer.status) ? (
            <div className="pt-2 border-t border-border text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Submitting…
            </div>
          ) : offer.status === 'countered' ? (
            <div className="pt-2 border-t border-border text-center text-xs font-medium text-amber-700">Awaiting carrier response…</div>
          ) : offer.status === 'accepted' ? (
            <div className="pt-2 border-t border-border flex items-center justify-between gap-2">
              <a href="/my-loads" className="text-xs font-semibold text-emerald-700 hover:underline">✓ Awarded — View in My Loads →</a>
              {dismissed.has(offer.id)
                ? <button type="button" onClick={() => handleRestore(offer.id)} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" />Restore</button>
                : <button type="button" onClick={() => handleClear(offer.id)} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Clear</button>}
            </div>
          ) : offer.status === 'rejected' ? (
            <div className="pt-2 border-t border-border flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">Declined</span>
              {dismissed.has(offer.id)
                ? <button type="button" onClick={() => handleRestore(offer.id)} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" />Restore</button>
                : <button type="button" onClick={() => handleClear(offer.id)} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Clear</button>}
            </div>
          ) : null}
        </CardContent>
      </Card>
    );
  };

  const renderDesktopRow = (offer: BrokerOffer) => {
    const locked = isLocked(offer.id);
    const showActions = isActionable(offer.status) && !locked;
    return (
      <tr key={offer.id} className={cn("border-b border-border hover:bg-muted/50", offer.status === 'accepted' && "bg-emerald-50/40")}>
        <td className="border-r border-border px-3 py-3">
          <span className="text-sm font-medium text-foreground">#{offer.load?.load_number}</span>
        </td>
        <td className="border-r border-border px-3 py-3 text-sm text-foreground">
          <div>{offer.load?.origin_city}, {offer.load?.origin_state}</div>
          <div className="text-muted-foreground">→ {offer.load?.destination_city}, {offer.load?.destination_state}</div>
        </td>
        <td className="border-r border-border px-3 py-3">
          <div className="text-sm font-medium text-foreground">{getCarrierName(offer)}</div>
          {offer.carrier_profile?.phone && (
            <div className="text-xs text-muted-foreground">{offer.carrier_profile.phone}</div>
          )}
        </td>
        <td className="border-r border-border px-3 py-3">
          <span className="text-lr-yellow font-bold">${offer.rate_amount.toLocaleString()}</span>
        </td>
        <td className="border-r border-border px-3 py-3 text-sm text-muted-foreground">
          ${offer.load?.rate_amount?.toLocaleString() || '-'}
        </td>
        <td className="border-r border-border px-3 py-3">
          <Badge variant="outline" className={cn("text-[10px]", STATUS_LABEL[offer.status]?.tone || 'bg-muted text-muted-foreground border-border')}>
            {STATUS_LABEL[offer.status]?.label || offer.status}
          </Badge>
          {offer.notes && (
            <div className="mt-1 text-[11px] text-muted-foreground italic max-w-[200px] truncate" title={offer.notes}>
              "{offer.notes}"
            </div>
          )}
        </td>
        <td className="border-r border-border px-3 py-3">
          {offer.expires_at && !isTerminal(offer.status) ? (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span>{getTimeRemaining(offer.expires_at)}</span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          )}
        </td>
        <td className="px-3 py-3">
          {showActions ? (
            <div className="flex items-center gap-1">
              <Button size="sm" className="bg-primary hover:bg-primary/85 text-primary-foreground h-7 text-xs px-2" onClick={() => showWarningOrRun(() => handleAccept(offer))} disabled={locked}>
                {locked ? <Loader2 className="w-3 h-3 animate-spin" /> : (<><Check className="w-3 h-3 mr-1" />Award</>)}
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => showWarningOrRun(() => handleCounterClick(offer))} disabled={locked}>
                Counter
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => showWarningOrRun(() => handleDeclineClick(offer))} disabled={locked}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          ) : locked && !isTerminal(offer.status) ? (
            <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Submitting…</span>
          ) : offer.status === 'countered' ? (
            <span className="text-[11px] font-medium text-amber-700">Awaiting carrier…</span>
          ) : offer.status === 'accepted' ? (
            <div className="flex items-center gap-2">
              <a href="/my-loads" className="text-xs font-semibold text-emerald-700 hover:underline">✓ Awarded — View →</a>
              {dismissed.has(offer.id)
                ? <button type="button" onClick={() => handleRestore(offer.id)} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5" title="Restore"><RotateCcw className="w-3 h-3" /></button>
                : <button type="button" onClick={() => handleClear(offer.id)} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5" title="Clear"><Trash2 className="w-3 h-3" /></button>}
            </div>
          ) : offer.status === 'rejected' ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Declined</span>
              {dismissed.has(offer.id)
                ? <button type="button" onClick={() => handleRestore(offer.id)} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5" title="Restore"><RotateCcw className="w-3 h-3" /></button>
                : <button type="button" onClick={() => handleClear(offer.id)} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5" title="Clear"><Trash2 className="w-3 h-3" /></button>}
            </div>
          ) : (
            <span className="text-[11px] text-muted-foreground">—</span>
          )}
        </td>
      </tr>
    );
  };

  const tableHeader = (
    <thead className="bg-muted">
      <tr>
        <th className="border-r border-border px-3 py-2 text-left text-xs font-medium text-muted-foreground">LOAD #</th>
        <th className="border-r border-border px-3 py-2 text-left text-xs font-medium text-muted-foreground">ROUTE</th>
        <th className="border-r border-border px-3 py-2 text-left text-xs font-medium text-muted-foreground">CARRIER</th>
        <th className="border-r border-border px-3 py-2 text-left text-xs font-medium text-muted-foreground">OFFER</th>
        <th className="border-r border-border px-3 py-2 text-left text-xs font-medium text-muted-foreground">YOUR RATE</th>
        <th className="border-r border-border px-3 py-2 text-left text-xs font-medium text-muted-foreground">STATUS</th>
        <th className="border-r border-border px-3 py-2 text-left text-xs font-medium text-muted-foreground">EXPIRES</th>
        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">ACTIONS</th>
      </tr>
    </thead>
  );

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">
            Carrier Offers ({activeOffers.length} active{historyOffers.length > 0 ? ` · ${historyOffers.length} history` : ''})
          </h3>
          <Button variant="outline" size="sm" onClick={refetch}>Refresh</Button>
        </div>



        {activeOffers.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              No active carrier offers right now.
            </CardContent>
          </Card>
        ) : isMobile ? (
          <div className="space-y-3">{activeOffers.map(renderMobileCard)}</div>
        ) : (
          <Card className="bg-card border-border">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                {tableHeader}
                <tbody>{activeOffers.map(renderDesktopRow)}</tbody>
              </table>
            </div>
          </Card>
        )}

        {(historyOffers.length > 0 || clearedCount > 0) && (
          <div>
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setShowHistory((s) => !s)}
                className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                History ({historyOffers.length})
              </button>
              {clearedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowCleared(s => !s)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {showCleared ? 'Hide cleared' : `Show cleared (${clearedCount})`}
                </button>
              )}
            </div>
            {showHistory && (
              isMobile ? (
                <div className="mt-2 space-y-3">{historyOffers.map(renderMobileCard)}</div>
              ) : (
                <Card className="mt-2 bg-card border-border">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      {tableHeader}
                      <tbody>{historyOffers.map(renderDesktopRow)}</tbody>
                    </table>
                  </div>
                </Card>
              )
            )}
          </div>
        )}
      </div>

      {/* Decline Confirmation Dialog */}
      <AlertDialog open={declineDialogOpen} onOpenChange={(open) => { setDeclineDialogOpen(open); if (!open) setDeclineNotes(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline Offer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to decline this offer from {selectedOffer && getCarrierName(selectedOffer)}?
              They will be notified that their offer was declined.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium text-foreground">Note to carrier <span className="text-muted-foreground font-normal">(optional)</span></label>
            <Textarea
              value={declineNotes}
              onChange={(e) => setDeclineNotes(e.target.value)}
              placeholder="Reason or feedback..."
              rows={2}
              maxLength={500}
              className="mt-1"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeclineConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Decline Offer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Counter Offer Dialog */}
      <AlertDialog open={counterDialogOpen} onOpenChange={setCounterDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Counter Offer</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedOffer && (
                <>
                  Carrier offered: <span className="font-bold text-lr-yellow">${selectedOffer.rate_amount.toLocaleString()}</span>
                  <br />
                  Your original rate: <span className="font-medium">${selectedOffer.load?.rate_amount?.toLocaleString() || '-'}</span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-3">
            <div>
              <label className="text-sm font-medium text-foreground">Your counter offer ($)</label>
              <Input
                type="number"
                value={counterAmount}
                onChange={(e) => setCounterAmount(e.target.value)}
                placeholder="Enter amount"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Note to carrier <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Textarea
                value={counterNotes}
                onChange={(e) => setCounterNotes(e.target.value)}
                placeholder="Best we can do, can move pickup, etc."
                rows={2}
                maxLength={500}
                className="mt-1"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCounterSubmit}
              disabled={!counterAmount || parseFloat(counterAmount) <= 0}
              className="bg-lr-cyan hover:bg-lr-cyan/90"
            >
              Send Counter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <VerificationWarningModal {...modalProps} />
    </>
  );
};

export default BrokerOffersSection;
